import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./button";

export interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Page numbers shown around the current page. Default 1. */
  siblingCount?: number;
  /** Total item count for the summary text (optional). */
  totalItems?: number;
  /** Label override for the summary. */
  getAriaLabel?: (page: number) => string;
}

/** Build `[1, '…', 4, 5, 6, '…', 20]`-style page slots. */
export function pageSlots(page: number, pageCount: number, siblingCount: number): (number | "gap")[] {
  const slots: (number | "gap")[] = [];
  const first = 1;
  const last = pageCount;
  const from = Math.max(first + 1, page - siblingCount);
  const to = Math.min(last - 1, page + siblingCount);
  slots.push(first);
  if (from > first + 1) slots.push("gap");
  for (let p = from; p <= to; p++) slots.push(p);
  if (to < last - 1) slots.push("gap");
  if (last > first) slots.push(last);
  return slots;
}

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  (
    {
      className,
      page,
      pageCount,
      onPageChange,
      siblingCount = 1,
      totalItems,
      getAriaLabel = (p) => `Page ${p}`,
      ...props
    },
    ref,
  ) => {
    if (pageCount <= 1 && totalItems == null) return null;
    const slots = pageSlots(page, Math.max(1, pageCount), siblingCount);
    return (
      <nav
        ref={ref}
        aria-label="Pagination"
        className={cn("flex items-center gap-1", className)}
        {...props}
      >
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <ol className="flex items-center gap-1">
          {slots.map((slot, i) =>
            slot === "gap" ? (
              <li key={`gap-${i}`}>
                <span aria-hidden="true" className="px-1 text-sm text-muted-foreground">
                  …
                </span>
              </li>
            ) : (
              <li key={slot}>
                <Button
                  type="button"
                  variant={slot === page ? "secondary" : "ghost"}
                  size="sm"
                  className="min-w-8 font-tnum"
                  aria-label={getAriaLabel(slot)}
                  aria-current={slot === page ? "page" : undefined}
                  onClick={() => onPageChange(slot)}
                >
                  {slot}
                </Button>
              </li>
            ),
          )}
        </ol>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Next page"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
        <span className="ml-2 hidden text-sm text-muted-foreground sm:inline">
          Page {page} of {pageCount}
        </span>
      </nav>
    );
  },
);
Pagination.displayName = "Pagination";

export { Pagination };
